import { Component, Inject, Input, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash';
import { StateService } from '@uirouter/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MediaService } from '@core/services/media.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'sfx-custom-fields-settings-list',
    templateUrl: './customfieldssetingslist.component.html',
    styleUrls: ['./customfieldssettingslist.component.scss']
})
export class CustomFieldsSettingsListComponent implements OnInit {

    standardFields: any[] = [];
    predefinedFields: any[] = [];
    customFields: any[] = [];

    pipelines: any[] = [];
    currentPipeline: any;

    doneLoading = false;

    displayNameTranslations: { [key: string]: string } = {};

    fieldNameTranslations: { [key: string]: string } = {};

    handleCustomFieldResponse = (response: any) => {

        this.standardFields = response.data.filter((field: any) => field.isStandardField);

        this.standardFields = this.translateFields(this.standardFields);

        this.predefinedFields = response.data.filter((field: any) => field.predefined_customfield).sort((a: any, b: any) => {

            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();

            if (aName < bName) {
                return -1;
            }

            if (aName > bName) {
                return 1;
            }

            return 0;
        });

        this.predefinedFields = this.translateFields(this.predefinedFields);

        this.customFields = response.data.filter((field: any) => !field.isStandardField && !field.predefined_customfield);

        this.customFields = this.translateFields(this.customFields);

        this.doneLoading = true;
    };

    constructor(
        @Inject('customFieldsService') public customFieldsService: any,
        @Inject('mdDialogService') public mdDialog: any,
        @Inject('modelService') public modelService: any,
        @Inject('filterService') public filterService: any,
        @Inject('pipelinesService') private pipelinesService: any,
        public stateService: StateService,
        public media: MediaService,
        private translate: TranslateService
    ) {
        this.displayNameTranslations = {
            'Text': this.translate.instant('Text'),
            'Number': this.translate.instant('Number'),
            'Date': this.translate.instant('Date'),
            'Long Text': this.translate.instant('Long Text'),
            'Dropdown': this.translate.instant('Dropdown'),
            'Multi Select': this.translate.instant('Multi Select'),
            'Checkbox': this.translate.instant('Checkbox'),
            'Files': this.translate.instant('Files'),
            'Autocomplete': this.translate.instant('Autocomplete'),
            'Contacts': this.translate.instant('Contacts'),
            'Account': this.translate.instant('Account'),
            'Accounts': this.translate.instant('Accounts'),
            'Tri-State Checkbox': this.translate.instant('Tri-State Checkbox'),
            'Multiselect dropdown': this.translate.instant('Multiselect dropdown'),
            'Tags': this.translate.instant('Tags')
        };

        this.fieldNameTranslations = {
            'Account name': this.translate.instant('Account name'),
            'Account website': this.translate.instant('Account website'),
            'Opportunity name': this.translate.instant('Opportunity name'),
            'Pipeline': this.translate.instant('Pipeline'),
            'Stage': this.translate.instant('Stage'),
            'Owner': this.translate.instant('Owner'),
            'Assignee': this.translate.instant('Assignee'),
            'Value': this.translate.instant('Value'),
            'Close date': this.translate.instant('Close date'),
            'Tags': this.translate.instant('Tags'),
            'Frequency': this.translate.instant('Frequency'),
            'Recurring price per unit': this.translate.instant('Recurring price per unit'),
            'Units': this.translate.instant('Units'),
            'Contract start date': this.translate.instant('Contract start date'),
            'Contract end date': this.translate.instant('Contract end date'),
            'Full name': this.translate.instant('Full name'),
            'Prefix': this.translate.instant('Prefix'),
            'First name': this.translate.instant('First name'),
            'Last name': this.translate.instant('Last name'),
            'Middle name': this.translate.instant('Middle name'),
            'Suffix': this.translate.instant('Suffix'),
            'Email': this.translate.instant('Email'),
            'Phone': this.translate.instant('Phone'),
            'Street': this.translate.instant('Street'),
            'Zip/Postal Code': this.translate.instant('Zip/Postal Code'),
            'City': this.translate.instant('City'),
            'State/Region': this.translate.instant('State/Region'),
            'Country': this.translate.instant('Country'),
            'Social profile URL': this.translate.instant('Social profile URL'),
            'Role': this.translate.instant('Role'),
            'Organisation': this.translate.instant('Organisation'),
            'Description': this.translate.instant('Description'),
            'Size': this.translate.instant('Size'),
            'Account email': this.translate.instant('Account email'),
            'Parent account': this.translate.instant('Parent account'),
            'Lost reason': this.translate.instant('Lost reason'),
            'Lead source': this.translate.instant('Lead source'),
            'Probability': this.translate.instant('Probability'),
            'Start date': this.translate.instant('Start date'),
            'Files': this.translate.instant('Files'),
            'Main contact': this.translate.instant('Main contact'),
            'Birth date': this.translate.instant('Birth date'),
            'Opt-out': this.translate.instant('Opt-out'),
            'Opt-out date': this.translate.instant('Opt-out date'),
            'Opt-out campaign': this.translate.instant('Opt-out campaign'),
            'Bounced': this.translate.instant('Bounced'),
            'Bounced date': this.translate.instant('Bounced date')
        };
    }

    @Input() itemClass: 'account' | 'contact' | 'opportunity' = 'account';

    public ngOnInit(): void {

        this.get();
    }

    public get = (): void => {

        this.doneLoading = false;

        switch (this.itemClass) {
            case 'opportunity':
                return this.pipelinesService.get().then((pipelinesResponse: any) => {

                    this.pipelines = pipelinesResponse.data;
                    const currentPipelineId = localStorage.getItem(`current_pipeline_${this.modelService.me.id}`);

                    if (currentPipelineId) {
                        this.currentPipeline = this.pipelines.find(function (pipeline) {

                            return pipeline.id.toString() === currentPipelineId;
                        });
                    }

                    if (!this.currentPipeline) {
                        this.filterService.setCurrentPipelineId(this.pipelines[0].id);
                        this.currentPipeline = this.pipelines[0];
                    }

                    return this.customFieldsService.getOpportunityFields(true, this.currentPipeline.id).then(this.handleCustomFieldResponse);
                });
            case 'account':
                return this.customFieldsService.getAccountFields(true).then(this.handleCustomFieldResponse);
            case 'contact':
                return this.customFieldsService.getContactFields(true).then(this.handleCustomFieldResponse);
        }
    }

    public changePipeline = (pipeline: any) => {

        this.currentPipeline = pipeline;
        this.filterService.setCurrentPipelineId(pipeline.id);

        return this.customFieldsService.getOpportunityFields(true, this.currentPipeline.id).then(this.handleCustomFieldResponse);
    };

    public comparePipelines = (o1: any, o2: any) => {

        return o1?.id === o2?.id;
    };

    public editCustomField = (customFieldId: number) => {

        return this.mdDialog.show({
            clickOutsideToClose: true,
            controller: 'EditCustomFieldController',
            templateUrl: 'partials/editcustomfield.html',
            locals: {
                id: customFieldId,
                itemClass: this.itemClass
            },
            bindToController: true,
            fullscreen: !this.media.isActive('gt-sm'),
            multiple: true
        }).then(this.get);
    };

    public disableOrEnableField = (field: any) => {

        field.enabled = !field.enabled;
        field.type = field.type.id;

        const updatedField = cloneDeep(field);
        delete updatedField.api_field;
        // No default values on update
        delete updatedField.default_boolean_value;
        // Type is not allowed on update
        delete updatedField.type;

        switch (this.itemClass) {
            case 'opportunity':
                return this.customFieldsService.updateOpportunityField(updatedField).then(this.get);
            case 'account':
                return this.customFieldsService.updateAccountField(updatedField).then(this.get);
            case 'contact':
                return this.customFieldsService.updateContactField(updatedField).then(this.get);
        }
    };

    public onDropCustomField = ($event: CdkDragDrop<string[]>) => {

        moveItemInArray(this.customFields, $event.previousIndex, $event.currentIndex);

        this.customFields.forEach((field: any, index: number) => {

            field.order = index + 1;
        });

        const orderUpdates = this.customFields.map((field: any) => {

            return {
                id: field.id,
                order: field.order
            };
        });

        switch (this.itemClass) {
            case 'opportunity':
                return this.customFieldsService.updateOpportunityOrder(orderUpdates).then(this.get);
            case 'account':
                return this.customFieldsService.updateAccountOrder(orderUpdates).then(this.get);
            case 'contact':
                return this.customFieldsService.updateContactOrder(orderUpdates).then(this.get);
        }
    }

    public createCustomField(): void {

        this.mdDialog.show({
            clickOutsideToClose: true,
            controller: 'EditCustomFieldController',
            templateUrl: 'partials/editcustomfield.html',
            locals: {
                itemClass: this.itemClass
            },
            bindToController: true,
            fullscreen: !this.media.isActive('gt-sm'),
            multiple: true
        }).then(this.get);
    }

    public getHideShowText(enabled: boolean): string {
        return enabled ? this.translate.instant('hide') : this.translate.instant('show');
    }

    private translateFields(fields: any): any {

        return fields.map((field: any) => {
            return {
                ...field,
                name: this.fieldNameTranslations[field.name] || field.name,
                type: {
                    ...field.type,
                    displayName: this.displayNameTranslations[field.type.displayName] || field.type.displayName
                }
            };
        });
    }
}
